import React, {Component, ReactNode} from 'react'
import { Box, OutlinedInput, Radio, RadioGroup, RadioProps } from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import {ATTENDANCE_STATUS, AVAILABLE_OR_NOT} from '../../CodeMaster'
import {Nullable} from '../../app/TypeHelpers'
import {DisplayItemInput} from '../../styledComponents/DisplayItem'
import {DialogCloseButton, DialogTitle, DisplayItemDividerDialog} from '../reservations/components/CommonStyle'
import {FlexGrowContent, LeftRowFlexContainer, RowFlexContainer} from '../../styledComponents/FlexComponents'
import {Close} from '@mui/icons-material'
import NewMeetingAttendanceResponse from './model/NewMeetingAttendanceResponse'
import {getStringOrEmptyString} from '../../__NoAutomatedTests/common/util/Natutil'
import MeetingAttendanceResponse from './model/MeetingAttendanceResponse'
import {PrimaryStyledButton} from '../../styledComponents/Button'
import {ErrorResponseBody} from '../../network/response/ErrorResponseBody'
import {Messages} from '../../Messages'
import {Colors, FontSize, FontWeights} from '../../styledComponents/Constants'
import {IconBox} from './AssocDirectorCommon'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faClock} from '@fortawesome/free-regular-svg-icons'
import {faMapMarkerAlt} from '@fortawesome/free-solid-svg-icons'
import styled, {css} from 'styled-components'

interface Props {
    meetingName: string
    meetingDateString: string
    meetingPlaceString: string
    proxyAttendanceAvailability: AVAILABLE_OR_NOT
    originalData: Nullable<MeetingAttendanceResponse>
    submitButtonLabel: string
    readOnly: boolean
    onClose: (isOk: boolean) => void
    onSubmit: (newMeetingAttendance: NewMeetingAttendanceResponse) =>
        Promise<'success' | 'communicationError' | ErrorResponseBody>
}

interface State {
    attendanceStatus: ATTENDANCE_STATUS
    proxyUserName: string
    proxyUserRelationship: string
    note: string
    readOnly: boolean
    errorTitle: Nullable<string>
    errorMessages: string[]
    proxyUserNameError: boolean
    proxyUserRelationshipError: boolean
    noteError: boolean
}

export default class ResidentMeetingAttendanceForm extends Component<Props, State> {
    constructor(props: Props) {
        super(props)

        const stateBase = {
            readOnly: props.readOnly,
            errorTitle: null,
            errorMessages: [],
            proxyUserNameError: false,
            proxyUserRelationshipError: false,
            noteError: false,
        }
        if (this.props.originalData) {
            const {
                originalData,
                proxyAttendanceAvailability,
            } = this.props

            let attendanceStatus = originalData.attendanceStatus
            if (proxyAttendanceAvailability === AVAILABLE_OR_NOT.UNAVAILABLE
                && attendanceStatus === ATTENDANCE_STATUS.PROXY_ATTEND) {
                attendanceStatus = ATTENDANCE_STATUS.UNANSWERED
            }

            this.state = {
                ...stateBase,
                attendanceStatus: attendanceStatus,
                proxyUserName: originalData.proxyUserName || '',
                proxyUserRelationship: originalData.proxyUserRelationship || '',
                note: originalData.note || '',
            }
        } else {
            this.state = {
                ...stateBase,
                attendanceStatus: ATTENDANCE_STATUS.UNANSWERED,
                proxyUserName: '',
                proxyUserRelationship: '',
                note: '',
            }
        }

        this.handleChangeNotes = this.handleChangeNotes.bind(this)
        this.handleFocusProxyTextForms = this.handleFocusProxyTextForms.bind(this)
        this.handleChangeProxyUserName = this.handleChangeProxyUserName.bind(this)
        this.handleChangeProxyUserRelationship = this.handleChangeProxyUserRelationship.bind(this)
        this.handleChangeAttendance = this.handleChangeAttendance.bind(this)
        this.handleClickSubmitButton = this.handleClickSubmitButton.bind(this)
        this.handleClickCloseButton = this.handleClickCloseButton.bind(this)
    }

    handleChangeAttendance(event: React.ChangeEvent<HTMLInputElement>): void {
        const attendance = ATTENDANCE_STATUS.codeToEnumConstants(event.target.value)
        this.setState({attendanceStatus: attendance})
    }

    handleFocusProxyTextForms(): void {
        this.setState({attendanceStatus: ATTENDANCE_STATUS.PROXY_ATTEND})
    }

    handleChangeProxyUserName(event: React.ChangeEvent<HTMLInputElement>): void {
        const proxyUserName = event.target.value || ''
        this.setState({proxyUserName})
    }

    handleChangeProxyUserRelationship(event: React.ChangeEvent<HTMLInputElement>): void {
        const proxyUserRelationship = event.target.value || ''
        this.setState({proxyUserRelationship})
    }

    handleChangeNotes(event: React.ChangeEvent<HTMLInputElement>): void {
        const note = event.target.value || ''
        this.setState({note})
    }

    async handleClickSubmitButton(): Promise<void> {
        const {attendanceStatus, proxyUserName, proxyUserRelationship, note} = this.state

        const newMeetingAttendance: NewMeetingAttendanceResponse = {
            attendanceStatus,
            proxyUserName: attendanceStatus === ATTENDANCE_STATUS.PROXY_ATTEND ? proxyUserName : null,
            proxyUserRelationship: attendanceStatus === ATTENDANCE_STATUS.PROXY_ATTEND ? proxyUserRelationship : null,
            note: note === '' ? null : note,
        }

        const result = await this.props.onSubmit(newMeetingAttendance)
        if (result === 'communicationError') {
            return
        }

        if (result === 'success') {
            this.props.onClose(true)
            return
        }

        const error = result
        if (error.requestItemErrors.length === 0) {
            this.setState({
                errorTitle: error.requestError.title,
                errorMessages: [error.requestError.message],
            })
        } else {
            const allErrorMessages = error.requestItemErrors.map(itemError => itemError.message)
            const errorMessages = Array.from(new Set<string>(allErrorMessages))
            const proxyUserNameError = error.requestItemErrors
                .some(itemError => itemError.fields?.includes('proxyUserName'))
            const proxyUserRelationshipError = error.requestItemErrors
                .some(itemError => itemError.fields?.includes('proxyUserRelationship'))
            const noteError = error.requestItemErrors
                .some(itemError => itemError.fields?.includes('note'))
            this.setState({
                errorTitle: Messages.EOPCR0001.toString('会議の出欠回答'),
                errorMessages: errorMessages,
                proxyUserNameError: proxyUserNameError,
                proxyUserRelationshipError: proxyUserRelationshipError,
                noteError: noteError,
            })
        }
    }

    handleClickCloseButton(): void {
        this.props.onClose(false)
    }

    isSubmitButtonEnabled(
        attendanceStatus: ATTENDANCE_STATUS,
        proxyUserName: string,
        proxyUserRelationship: string,
    ): boolean {
        if (attendanceStatus === ATTENDANCE_STATUS.UNANSWERED) {
            return false
        }
        if (attendanceStatus === ATTENDANCE_STATUS.PROXY_ATTEND &&
            (proxyUserName.trim() === '' || proxyUserRelationship.trim() === '')) {
            return false
        }

        return true
    }

    render(): ReactNode {
        const {
            meetingName,
            meetingDateString,
            meetingPlaceString,
            proxyAttendanceAvailability,
            originalData,
            submitButtonLabel,
        } = this.props
        const {
            attendanceStatus,
            proxyUserName,
            proxyUserRelationship,
            note,
        } = this.state

        const disableSubmitButton: boolean =
            !this.isSubmitButtonEnabled(attendanceStatus, proxyUserName, proxyUserRelationship)

        return (<>
            <Box m={'10px'}>
                <RowFlexContainer>
                    <DialogTitle>出欠回答</DialogTitle>
                    <DialogCloseButton onClick={this.handleClickCloseButton} id={'close'}>
                        <Close fontSize={'large'}/>
                    </DialogCloseButton>
                </RowFlexContainer>
            </Box>
            <DisplayItemDividerDialog/>
            <AttendanceFormArea>
                {
                    this.state.errorTitle && <>
                        <AlertArea>
                            <AlertTitle>{this.state.errorTitle}</AlertTitle>
                            {this.state.errorMessages.map((message, index) =>
                                <AlertText key={index}>{message}</AlertText>)}
                        </AlertArea>
                        <Box height={'15px'}/>
                    </>
                }
                <LeftRowFlexContainer>
                    <MeetingIcon/>
                    <Box fontWeight={FontWeights.Bold}>
                        {meetingName}
                    </Box>
                </LeftRowFlexContainer>
                <Box height={'5px'}/>
                <LeftRowFlexContainer>
                    <IconBox><FontAwesomeIcon icon={faClock}/></IconBox>
                    {meetingDateString}
                </LeftRowFlexContainer>
                <Box height={'5px'}/>
                <LeftRowFlexContainer>
                    <IconBox><FontAwesomeIcon icon={faMapMarkerAlt}/></IconBox>
                    <MeetingPlaceText>{meetingPlaceString}</MeetingPlaceText>
                </LeftRowFlexContainer>
                <Box height={'21px'}>
                    {
                        originalData !== null && originalData.agentRespondentAdminId !== null &&
                        <AgentRespondentName>
                            代行回答者： {originalData.agentRespondentName}（{originalData.agentRespondentRoleName}）
                        </AgentRespondentName>
                    }
                </Box>
                <RadioGroup
                    value={attendanceStatus !== ATTENDANCE_STATUS.UNANSWERED ? attendanceStatus.value : null}
                    onChange={this.handleChangeAttendance}
                >
                    <AttendanceBackdrop selected={attendanceStatus === ATTENDANCE_STATUS.ATTEND}>
                        <AttendanceStyles
                            selected={attendanceStatus === ATTENDANCE_STATUS.ATTEND}
                            readOnly={this.state.readOnly}
                        >
                            <LeftRowFlexContainer>
                                <AttendanceRadio
                                    value={ATTENDANCE_STATUS.ATTEND.value}
                                    disabled={this.state.readOnly}
                                />
                                {ATTENDANCE_STATUS.ATTEND.label}
                            </LeftRowFlexContainer>
                        </AttendanceStyles>
                    </AttendanceBackdrop>
                    <Box height={'10px'}/>
                    {
                        proxyAttendanceAvailability === AVAILABLE_OR_NOT.AVAILABLE &&
                        <>
                            <AttendanceBackdrop selected={attendanceStatus === ATTENDANCE_STATUS.PROXY_ATTEND}>
                                <AttendanceStyles
                                    selected={attendanceStatus === ATTENDANCE_STATUS.PROXY_ATTEND}
                                    readOnly={this.state.readOnly}
                                >
                                    <LeftRowFlexContainer>
                                        <AttendanceRadio
                                            value={ATTENDANCE_STATUS.PROXY_ATTEND.value}
                                            disabled={this.state.readOnly}
                                        />
                                        {ATTENDANCE_STATUS.PROXY_ATTEND.label}
                                    </LeftRowFlexContainer>
                                    <Box height={'10px'}/>
                                    <label>
                                        <InputLabel>代理人氏名</InputLabel>
                                        <DisplayItemInputThin
                                            type={'text'}
                                            minLength={0}
                                            maxLength={25}
                                            value={getStringOrEmptyString(proxyUserName)}
                                            readOnly={this.state.readOnly}
                                            hasError={this.state.proxyUserNameError}
                                            onChange={this.handleChangeProxyUserName}
                                            onFocus={!this.state.readOnly ? this.handleFocusProxyTextForms : undefined}
                                        />
                                    </label>
                                    <Box height={'5px'}/>
                                    <label>
                                        <InputLabel>続柄</InputLabel>
                                        <DisplayItemInputThin
                                            type={'text'}
                                            minLength={0}
                                            maxLength={15}
                                            value={getStringOrEmptyString(proxyUserRelationship)}
                                            readOnly={this.state.readOnly}
                                            hasError={this.state.proxyUserRelationshipError}
                                            onChange={this.handleChangeProxyUserRelationship}
                                            onFocus={!this.state.readOnly ? this.handleFocusProxyTextForms : undefined}
                                        />
                                    </label>
                                </AttendanceStyles>
                            </AttendanceBackdrop>
                            <Box height={'10px'}/>
                        </>
                    }
                    <AttendanceBackdrop selected={attendanceStatus === ATTENDANCE_STATUS.ABSENT}>
                        <AttendanceStyles
                            selected={attendanceStatus === ATTENDANCE_STATUS.ABSENT}
                            readOnly={this.state.readOnly}
                        >
                            <LeftRowFlexContainer>
                                <AttendanceRadio
                                    value={ATTENDANCE_STATUS.ABSENT.value}
                                    disabled={this.state.readOnly}
                                />
                                {ATTENDANCE_STATUS.ABSENT.label}
                            </LeftRowFlexContainer>
                        </AttendanceStyles>
                    </AttendanceBackdrop>
                </RadioGroup>
                <Box height={'15px'}/>
                <label>
                    <InputLabel>連絡事項</InputLabel>
                    <StyledOutlinedInput
                        multiline
                        maxRows={4.5}
                        fullWidth
                        margin={'dense'}
                        inputProps={{
                            minLength: 0,
                            maxLength: 100,
                        }}
                        value={getStringOrEmptyString(note)}
                        readOnly={this.state.readOnly}
                        error={this.state.noteError}
                        onChange={this.handleChangeNotes}
                    />
                </label>
                <FlexGrowContent/>
                {
                    !this.state.readOnly && <>
                        <Box height={'30px'}/>
                        <div>
                            <PrimaryStyledButton
                                onClick={this.handleClickSubmitButton}
                                disabled={disableSubmitButton}>
                                {submitButtonLabel}
                            </PrimaryStyledButton>
                        </div>
                    </>
                }
            </AttendanceFormArea>
        </>)
    }
}

const AttendanceFormArea = styled.div`
  display: flex;
  flex-direction: column;
  padding: 15px;
  color: ${Colors.Body};
  flex-grow: 1;
`

const AlertArea = styled.div`
  padding: 10px 11px;
  background-color: rgb(253, 245, 246);
`

const AlertTitle = styled.div`
  color: ${Colors.Alert};
  font-weight: ${FontWeights.Bold};
`

const AlertText = styled.div`
  font-size: ${FontSize.Middle};
`

const MeetingIcon = styled.span`
  width: 16px;
  height: 16px;
  margin-right: 5px;
  display: inline-block;
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
  background-image: url('/resources/images/assoc/assoc_meeting_black.svg');
  transform: translateY(1px);
`

const MeetingPlaceText = styled.span`
  font-size: ${FontSize.Middle};
`

const AttendanceBackdrop = styled.label<{ selected: boolean }>`
  padding: 13px 15px;
  background-color: ${props => {
    if (props.selected) {
      return 'rgba(0, 112, 176, 0.15)'
    } else {
      return 'rgba(241, 241, 241, 1)'
    }
  }};
`

const AttendanceRadio = withStyles({
    root: {
        padding: 0,
        marginLeft: '-3px',
        marginRight: '10px',
        color: Colors.Body,
        '&$checked': {
            color: Colors.Primary,
        },
        '& .MuiSvgIcon-root': {
            fontSize: '22px',
        },
    },
    checked: {},
})
((props: RadioProps) => <Radio color="default" {...props} />)

const ReadOnlyNotSelectStyles = css`
  color: ${Colors.AccentDark};
  opacity: 0.5;
`

const AttendanceStyles = styled.div<{ selected: boolean, readOnly: boolean }>`
  ${props => {
    if (!props.selected && props.readOnly) {
      return ReadOnlyNotSelectStyles
    } else {
      return ''
    }
  }}
`

const InputLabel = styled.div`
  font-size: ${FontSize.Middle};
  color: ${Colors.AccentDark};
`

const DisplayItemInputThin = styled(DisplayItemInput)`
  height: 40px;
`

const StyledOutlinedInput = withStyles({
    root: {
        '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: Colors.AccentLight,
        },
        fontSize: FontSize.Body,
        '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: Colors.AccentDark,
            borderWidth: '1px',
        },
        '&.MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: Colors.Alert,
            backgroundColor: 'rgb(253, 245, 246)',
        },
    },
    input: {
        zIndex: 10,
        fontFamily: '"Noto Sans JP", sans-serif',
        lineHeight: 1.5,
    },
    notchedOutline: {
        border: `${Colors.AccentLight} 1px solid`,
    },
})
(OutlinedInput)

const AgentRespondentName = styled.div`
  font-size: ${FontSize.Small};
  color: ${Colors.AccentDark};
  text-align: right;
`
